import {
  FORMATOS_PUBLICACION,
  HORAS_PUBLICACION,
  HORA_PUBLICACION_DEFAULT,
  PILARES_PUBLICACION,
  REDES_PUBLICACION,
  fueraDeHorizonte,
  publicacionCrearSchema,
  type FormatoPublicacion,
  type ModoPublicacion,
  type PilarPublicacion,
  type PublicacionCreada,
  type PublicacionPublic,
  type RedPublicacion,
} from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { RedSocialIcon } from '../../../components/icons/RedSocialIcon';
import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { publicacionesApi } from '../api/publicaciones.api';
import { Calendario } from '../components/Calendario';
import { ESTADO_CLASE, fechaLinda } from '../components/estados';
import { IndicadoresContenido } from '../components/IndicadoresContenido';
import { SelectorArchivos } from '../components/SelectorArchivos';

// El Publicador (ciclo-panel, R4): reemplaza la carga a mano en Notion. Tres
// pestañas (cargar / log / calendario), sin encabezado, y todo entra en una
// pantalla: lo único que scrollea es la lista interna. Valida con el MISMO
// schema que el backend (las reglas de MAESTRO) antes de habilitar el botón.

type Pestana = 'cargar' | 'log' | 'calendario' | 'indicadores';

interface Form {
  titulo: string;
  caption: string;
  hashtags: string;
  formato: FormatoPublicacion;
  redes: RedPublicacion[];
  pilar: PilarPublicacion | '';
  modo: ModoPublicacion;
  fecha: string;
  hora: string;
  notas: string;
}

const VACIO: Form = {
  titulo: '',
  caption: '',
  hashtags: '',
  formato: 'REEL',
  redes: [],
  pilar: '',
  modo: 'PROGRAMAR',
  fecha: '',
  hora: HORA_PUBLICACION_DEFAULT,
  notas: '',
};

// El alto de la pantalla menos el header y el footer del layout: la página no scrollea.
const ALTO = 'h-[calc(100vh-7.5rem)]';
const CAMPO =
  'flex h-9 w-full rounded-md border border-marca-neutro bg-surface px-3 text-sm text-marca-gris focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marca-primario';
const ROTULO = 'text-xs font-medium text-text-subtle';

export function PublicarPage(): JSX.Element {
  const es = useI18n();
  const t = es.publicar;
  const qc = useQueryClient();
  const [pestana, setPestana] = useState<Pestana>('cargar');
  const [form, setForm] = useState<Form>(VACIO);
  const [archivos, setArchivos] = useState<File[]>([]);
  const [progreso, setProgreso] = useState(0);
  const [creada, setCreada] = useState<PublicacionCreada | null>(null);

  const set = <K extends keyof Form>(k: K, v: Form[K]): void => setForm((f) => ({ ...f, [k]: v }));

  const faltantes = useMemo(() => {
    const r = publicacionCrearSchema.safeParse({
      ...form,
      pilar: form.pilar || null,
      fecha: form.fecha || null,
    });
    const lista = r.success ? [] : Array.from(new Set(r.error.issues.map((i) => i.message)));
    if (archivos.length === 0)
      lista.push(form.formato === 'CARRUSEL' ? t.archivo.ayudaCarrusel : t.archivo.etiqueta);
    return lista;
  }, [form, archivos, t]);
  const valido = faltantes.length === 0;

  const crear = useMutation({
    mutationFn: () =>
      publicacionesApi.crear(
        { ...form, pilar: form.pilar || null, fecha: form.modo === 'PROGRAMAR' ? form.fecha : null },
        archivos,
        setProgreso,
      ),
    onSuccess: (r) => {
      setCreada(r);
      setForm(VACIO);
      setArchivos([]);
      setProgreso(0);
      void qc.invalidateQueries({ queryKey: qk.publicaciones.all });
    },
    onError: () => setProgreso(0),
  });

  const errorTexto = (e: unknown): string => {
    if (axios.isAxiosError(e)) {
      const code = (e.response?.data as { error?: { code?: string } } | undefined)?.error?.code;
      if (code === 'DRIVE_NO_CONFIGURADO') return t.errores.driveNoConfigurado;
      if (code === 'DRIVE_ERROR') return t.errores.drive;
    }
    return getErrorMessage(e, es.errores.generico.algoSalioMal);
  };

  const redesTexto = (redes: RedPublicacion[]): string => redes.map((r) => t.redes[r]).join(', ');
  const mensajeListo = (c: PublicacionCreada): string => {
    if (c.modo === 'PUBLICAR_YA' || !c.fecha) return t.listo.ya.replace('{redes}', redesTexto(c.redes));
    const base = c.fueraDeHorizonte ? t.listo.horizonte : t.listo.programado;
    return base
      .replace('{fecha}', fechaLinda(c.fecha))
      .replace('{hora}', c.hora)
      .replace('{redes}', redesTexto(c.redes));
  };
  const avisoHorizonte =
    form.modo === 'PROGRAMAR' && form.fecha && valido && fueraDeHorizonte(form.fecha, form.hora)
      ? t.listo.horizonte
          .replace('{fecha}', fechaLinda(form.fecha))
          .replace('{hora}', form.hora)
          .replace('Listo: ', '')
      : null;

  const Opcion = ({
    activa,
    onClick,
    children,
  }: {
    activa: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }): JSX.Element => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={cn(
        'rounded-md border px-2 py-1.5 text-sm font-medium transition-colors',
        activa
          ? 'border-marca-primario bg-surface-primario text-marca'
          : 'border-border-subtle text-text-subtle hover:border-border-strong',
      )}
    >
      {children}
    </button>
  );

  return (
    <div className={cn('mx-auto flex max-w-6xl flex-col px-6 pt-3', ALTO)}>
      <Tabs
        value={pestana}
        onChange={(v) => setPestana(v as Pestana)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList>
          <TabsTrigger value="cargar">{t.pestanas.cargar}</TabsTrigger>
          <TabsTrigger value="calendario">{t.pestanas.calendario}</TabsTrigger>
          <TabsTrigger value="log">{t.pestanas.log}</TabsTrigger>
          <TabsTrigger value="indicadores">{t.pestanas.indicadores}</TabsTrigger>
        </TabsList>

        <TabsContent value="cargar" className="mt-3 min-h-0 flex-1">
          {creada && (
            <Alert variant="success" className="mb-3">
              <div className="flex flex-wrap items-center gap-3">
                <CheckCircle2 size={18} className="shrink-0" />
                <span className="flex-1">{mensajeListo(creada)}</span>
                <Button size="sm" variant="outline" onClick={() => setCreada(null)}>
                  {t.listo.otra}
                </Button>
              </div>
            </Alert>
          )}
          <form
            className="grid h-full gap-4 lg:grid-cols-[1.15fr_1fr]"
            onSubmit={(e) => {
              e.preventDefault();
              if (valido && !crear.isPending) crear.mutate();
            }}
          >
            {/* Qué y el archivo */}
            <div className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-lg border border-border-subtle bg-surface p-4 shadow-card">
              <div className="grid grid-cols-3 gap-2">
                {FORMATOS_PUBLICACION.map((f) => (
                  <Opcion
                    key={f}
                    activa={form.formato === f}
                    onClick={() => {
                      set('formato', f);
                      setArchivos([]);
                    }}
                  >
                    {t.formato[f]}
                  </Opcion>
                ))}
              </div>
              <SelectorArchivos formato={form.formato} archivos={archivos} onChange={setArchivos} compacto />
              <label className="flex flex-col gap-1">
                <span className={ROTULO}>{t.tituloCampo}</span>
                <Input
                  className="h-9"
                  value={form.titulo}
                  onChange={(e) => set('titulo', e.target.value)}
                  maxLength={120}
                  aria-label={t.tituloCampo}
                />
              </label>
              <label className="flex min-h-0 flex-1 flex-col gap-1">
                <span className={ROTULO}>
                  {t.caption}{' '}
                  <span className="text-text-faint">
                    · {t.captionContador.replace('{n}', String(form.caption.length))}
                  </span>
                </span>
                <Textarea
                  className="min-h-[72px] flex-1 resize-none"
                  value={form.caption}
                  onChange={(e) => set('caption', e.target.value)}
                  maxLength={2200}
                  aria-label={t.caption}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className={ROTULO}>{t.hashtags}</span>
                <Input
                  className="h-9"
                  value={form.hashtags}
                  onChange={(e) => set('hashtags', e.target.value)}
                  maxLength={500}
                  aria-label={t.hashtags}
                />
              </label>
            </div>

            {/* Dónde, cuándo y publicar */}
            <div className="flex min-h-0 flex-col gap-3 self-start overflow-y-auto rounded-lg border border-border-subtle bg-surface p-4 shadow-card">
              <fieldset className="flex flex-col gap-1.5">
                <legend className={ROTULO}>{t.redes.etiqueta}</legend>
                <div className="grid grid-cols-4 gap-2">
                  {REDES_PUBLICACION.map((r) => {
                    const marcada = form.redes.includes(r);
                    return (
                      <label
                        key={r}
                        className={cn(
                          'flex cursor-pointer items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-sm transition-colors',
                          marcada
                            ? 'border-marca-primario bg-surface-primario text-marca'
                            : 'border-border-subtle hover:border-border-strong',
                        )}
                      >
                        <input
                          type="checkbox"
                          className="accent-marca-primario"
                          checked={marcada}
                          onChange={(e) =>
                            set(
                              'redes',
                              e.target.checked ? [...form.redes, r] : form.redes.filter((x) => x !== r),
                            )
                          }
                        />
                        <RedSocialIcon red={r} size={14} />
                        {t.redes[r]}
                      </label>
                    );
                  })}
                </div>
                {form.redes.includes('YOUTUBE') && form.formato !== 'REEL' && (
                  <span className="text-xs text-warning">{t.redes.ayudaYoutube}</span>
                )}
              </fieldset>

              <fieldset className="flex flex-col gap-1.5">
                <legend className={ROTULO}>{t.modo.etiqueta}</legend>
                <div className="grid grid-cols-2 gap-2">
                  {(['PROGRAMAR', 'PUBLICAR_YA'] as const).map((m) => (
                    <Opcion key={m} activa={form.modo === m} onClick={() => set('modo', m)}>
                      {t.modo[m]}
                    </Opcion>
                  ))}
                </div>
              </fieldset>

              {form.modo === 'PROGRAMAR' && (
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex flex-col gap-1">
                    <span className={ROTULO}>{t.fecha}</span>
                    <Input
                      className="h-9"
                      type="date"
                      value={form.fecha}
                      onChange={(e) => set('fecha', e.target.value)}
                      aria-label={t.fecha}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className={ROTULO}>{t.hora}</span>
                    <select
                      className={CAMPO}
                      value={form.hora}
                      onChange={(e) => set('hora', e.target.value)}
                      aria-label={t.hora}
                    >
                      {HORAS_PUBLICACION.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              {avisoHorizonte && <span className="text-xs text-text-subtle">{avisoHorizonte}</span>}

              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1">
                  <span className={ROTULO}>{t.pilar.etiqueta}</span>
                  <select
                    className={CAMPO}
                    value={form.pilar}
                    onChange={(e) => set('pilar', e.target.value as PilarPublicacion | '')}
                    aria-label={t.pilar.etiqueta}
                  >
                    <option value="">{t.pilar.ninguno}</option>
                    {PILARES_PUBLICACION.map((p) => (
                      <option key={p} value={p}>
                        {t.pilar[p]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className={ROTULO}>
                    {t.notas} <span className="text-text-faint">· {t.notasAyuda}</span>
                  </span>
                  <Input
                    className="h-9"
                    value={form.notas}
                    onChange={(e) => set('notas', e.target.value)}
                    maxLength={1000}
                    aria-label={t.notas}
                  />
                </label>
              </div>

              <div className="flex flex-col gap-2 border-t border-border-subtle pt-3">
                {!valido && (
                  <div className="text-xs text-text-subtle">
                    <span className="font-medium">{t.faltan}</span> {faltantes.join(' · ')}
                  </div>
                )}
                {crear.isError && <Alert variant="error">{errorTexto(crear.error)}</Alert>}
                {crear.isPending && (
                  <div
                    className="h-2 overflow-hidden rounded-full bg-surface-muted"
                    aria-label={t.subiendo.replace('{pct}', String(progreso))}
                  >
                    <div
                      className="h-full bg-marca-primario transition-all"
                      style={{ width: `${String(progreso)}%` }}
                    />
                  </div>
                )}
                <Button type="submit" size="lg" disabled={!valido || crear.isPending} className="w-full">
                  {crear.isPending ? t.subiendo.replace('{pct}', String(progreso)) : t.boton}
                </Button>
              </div>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="log" className="mt-3 min-h-0 flex-1">
          <Log errorTexto={errorTexto} />
        </TabsContent>

        <TabsContent value="calendario" className="mt-3 min-h-0 flex-1">
          <Calendario className="h-full" />
        </TabsContent>

        <TabsContent value="indicadores" className="mt-3 min-h-0 flex-1">
          <IndicadoresContenido />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Log({ errorTexto }: { errorTexto: (e: unknown) => string }): JSX.Element {
  const es = useI18n();
  const t = es.publicar.lista;
  const lista = useQuery({
    queryKey: qk.publicaciones.lista,
    queryFn: () => publicacionesApi.listar({ limite: 60 }),
  });
  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-border-subtle bg-surface shadow-card">
      {lista.isLoading && <p className="p-4 text-sm text-text-subtle">{t.cargando}</p>}
      {lista.isError && (
        <Alert variant="error" className="m-3">
          {errorTexto(lista.error)}
        </Alert>
      )}
      {lista.data && lista.data.length === 0 && <p className="p-4 text-sm text-text-subtle">{t.vacio}</p>}
      {lista.data && lista.data.length > 0 && (
        <ul className="min-h-0 flex-1 divide-y divide-border-subtle overflow-y-auto">
          {lista.data.map((p) => (
            <Fila key={p.id} p={p} estados={t.estados} sinFecha={t.sinFecha} />
          ))}
        </ul>
      )}
    </div>
  );
}

// ciclo-contenido-sin-notion: sin el link a la página de Notion (ya no existe).
function Fila({
  p,
  estados,
  sinFecha,
}: {
  p: PublicacionPublic;
  estados: Record<string, string>;
  sinFecha: string;
}): JSX.Element {
  return (
    <li className="flex items-center gap-3 px-4 py-2 text-sm">
      <span
        className={cn(
          'w-24 shrink-0 rounded-full px-2 py-0.5 text-center text-xs font-medium',
          ESTADO_CLASE[p.estado] ?? ESTADO_CLASE.Borrador,
        )}
      >
        {estados[p.estado] ?? p.estado}
      </span>
      <span className="w-28 shrink-0 text-xs text-text-subtle">
        {p.fecha ? `${fechaLinda(p.fecha)} ${p.hora ?? ''}` : sinFecha}
      </span>
      <span className="min-w-0 flex-1 truncate font-medium text-text-strong">{p.titulo}</span>
      <span className="hidden shrink-0 text-xs text-text-subtle md:inline">
        {p.formato ?? ''} · {p.redes.join(', ')}
      </span>
    </li>
  );
}
